import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorResponse, PaginatedResponse } from '../api';
import {
  createNewJourney,
  fetchHealthJourneys,
  fetchHealthPicture,
  updateHealthJourney,
  deleteHealthJourney,
  updateHealthPicture,
  fetchHealthJourneyTimeline,
  deleteHistoricalHealthPicture,
  fetchSymptomCheckerResults,
} from './actions';
import { Consultation } from './consultation/models';
import { Journal } from './journal/model';
import {
  HealthJourney,
  HealthPictureItem,
  SymptomCheckerResult,
} from './model';
import { deleteConsultation } from './consultation/actions';
import { deleteJournal } from './journal/actions';
import { HEALTH_JOURNEY_MAIN_TABS } from './helpers';
import type { MyCareChangeParams } from '../types/signlaRTypes';

export interface HealthJourneyState {
  loadingHealthJourneys: boolean;
  healthJourneys: HealthJourney[];
  selectedHealthJourney?: HealthJourney;
  healthPicture?: HealthPictureItem;
  loadingHealthPicture: boolean;
  errorHealthPicture?: ErrorResponse;
  healthJourneyTimeline: PaginatedResponse<
    (HealthPictureItem | Consultation | Journal)[]
  >;
  loadingHealthJourneyTimeline: boolean;
  errorHealthJourneyTimeline?: ErrorResponse;
  currentHealthPicture?: HealthPictureItem;
  isOpenCreateJourneyDialog: boolean;
  symptomCheckerResults: SymptomCheckerResult[];
  loadingSymptomCheckerResults: boolean;
  preselectHealthJourneyTab?: HEALTH_JOURNEY_MAIN_TABS;
  /** Latest per-item MyCare change directive from SignalR (for toast + highlight). */
  myCareChangeEvent?: MyCareChangeParams;
}

const initialState: HealthJourneyState = {
  loadingHealthJourneys: true,
  healthJourneys: [],
  selectedHealthJourney: undefined,
  healthPicture: undefined,
  loadingHealthPicture: false,
  errorHealthPicture: undefined,
  healthJourneyTimeline: { data: [] },
  loadingHealthJourneyTimeline: false,
  errorHealthJourneyTimeline: undefined,
  currentHealthPicture: undefined,
  isOpenCreateJourneyDialog: false,
  symptomCheckerResults: [],
  loadingSymptomCheckerResults: false,
  preselectHealthJourneyTab: undefined,
  myCareChangeEvent: undefined,
};

const healthJourneySlice = createSlice({
  name: 'healthJourney',
  initialState,
  reducers: {
    setSelectedHealthJourney: (
      state,
      action: PayloadAction<HealthJourney | undefined>
    ) => {
      state.selectedHealthJourney = action.payload;
    },
    clearHealthPicture: (state) => {
      state.healthPicture = undefined;
    },
    clearTimeline: (state) => {
      state.healthJourneyTimeline.data = [];
      state.healthJourneyTimeline.xMsContinuationToken = undefined;
    },
    setCurrentHealthPicture: (
      state,
      action: PayloadAction<HealthPictureItem | undefined>
    ) => {
      state.currentHealthPicture = action.payload;
    },
    setIsOpenCreateJourneyDialog: (state, action: PayloadAction<boolean>) => {
      state.isOpenCreateJourneyDialog = action.payload;
    },
    setPreselectedHealthJourneyTab: (
      state,
      action: PayloadAction<HEALTH_JOURNEY_MAIN_TABS | undefined>
    ) => {
      state.preselectHealthJourneyTab = action.payload;
    },
    setMyCareChangeEvent: (
      state,
      action: PayloadAction<MyCareChangeParams>
    ) => {
      state.myCareChangeEvent = action.payload;
    },
    clearMyCareChangeEvent: (state) => {
      state.myCareChangeEvent = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createNewJourney.fulfilled, (state, action) => {
        state.healthJourneys = action.payload;
        state.loadingHealthJourneys = false;

        if (state.healthJourneys?.length > 0) {
          state.selectedHealthJourney =
            state.healthJourneys[state.healthJourneys.length - 1];
        }
      })
      .addCase(createNewJourney.pending, (state) => {
        state.loadingHealthJourneys = true;
      })
      .addCase(createNewJourney.rejected, (state) => {
        state.loadingHealthJourneys = false;
      })
      .addCase(fetchHealthJourneys.pending, (state) => {
        state.loadingHealthJourneys = true;
      })
      .addCase(fetchHealthJourneys.fulfilled, (state, action) => {
        if (action.payload) {
          state.healthJourneys = action.payload;

          if (
            state.selectedHealthJourney?.relationship === 'me' &&
            state.healthJourneys.length > 0
          ) {
            state.selectedHealthJourney = state.healthJourneys.find(
              (healthJourney) => healthJourney.relationship === 'me'
            )
              ? state.healthJourneys.find(
                  (healthJourney) => healthJourney.relationship === 'me'
                )
              : state.selectedHealthJourney;
          }

          if (
            !state.selectedHealthJourney &&
            state.healthJourneys?.length > 0
          ) {
            state.selectedHealthJourney = state.healthJourneys[0];
          }
        } else {
          state.healthJourneys = [];
        }
        state.loadingHealthJourneys = false;
      })
      .addCase(updateHealthJourney.fulfilled, (state, action) => {
        state.healthJourneys = action.payload;
        const updatedHealthJourney = action.payload.find(
          (journey) => journey.id === state.selectedHealthJourney?.id
        );
        if (updatedHealthJourney !== undefined) {
          state.selectedHealthJourney = updatedHealthJourney;
        }
      })
      .addCase(deleteHealthJourney.fulfilled, (state, action) => {
        state.healthJourneys = [
          ...state.healthJourneys.filter(
            (healthJourney) => healthJourney.id !== action.meta.arg.journeyId
          ),
        ];
        if (state.selectedHealthJourney?.id === action.meta.arg.journeyId) {
          state.selectedHealthJourney = state.healthJourneys.length
            ? state.healthJourneys[0]
            : undefined;
        }
      })
      .addCase(fetchHealthPicture.fulfilled, (state, action) => {
        state.healthPicture = action.payload;
        state.loadingHealthPicture = false;
      })
      .addCase(fetchHealthPicture.pending, (state) => {
        state.loadingHealthPicture = true;
      })
      .addCase(fetchHealthPicture.rejected, (state, action) => {
        state.errorHealthPicture = action.payload;
        state.loadingHealthPicture = false;
      })
      .addCase(updateHealthPicture.fulfilled, (state, action) => {
        state.healthPicture = action.payload;
        state.loadingHealthPicture = false;
      })
      .addCase(updateHealthPicture.pending, (state) => {
        state.loadingHealthPicture = true;
      })
      .addCase(updateHealthPicture.rejected, (state, action) => {
        state.errorHealthPicture = action.payload;
        state.loadingHealthPicture = false;
      })
      .addCase(fetchHealthJourneyTimeline.fulfilled, (state, action) => {
        let timeline = state.healthJourneyTimeline.data || [];

        if (action.meta.arg.xMsContinuationToken) {
          timeline.push(...action.payload.data);
        } else {
          timeline = [...action.payload.data];
        }
        state.healthJourneyTimeline = {
          data: timeline,
          xMsContinuationToken: action.payload.xMsContinuationToken,
        };
        state.loadingHealthJourneyTimeline = false;
        state.errorHealthJourneyTimeline = undefined;
      })
      .addCase(fetchHealthJourneyTimeline.pending, (state) => {
        state.loadingHealthJourneyTimeline = true;
      })
      .addCase(fetchHealthJourneyTimeline.rejected, (state, action) => {
        state.loadingHealthJourneyTimeline = false;
        state.errorHealthJourneyTimeline = action.payload;
      })
      .addCase(deleteHistoricalHealthPicture.fulfilled, (state, action) => {
        state.healthJourneyTimeline.data = state.healthJourneyTimeline.data.filter(
          (healthPicture) => healthPicture.id !== action.meta.arg
        );
      })
      .addCase(deleteConsultation.fulfilled, (state, action) => {
        state.healthJourneyTimeline.data = state.healthJourneyTimeline.data.filter(
          (item) => item.id !== action.meta.arg
        );
      })
      .addCase(deleteJournal.fulfilled, (state, action) => {
        state.healthJourneyTimeline.data = state.healthJourneyTimeline.data.filter(
          (item) => item.id !== action.meta.arg
        );
      })
      .addCase(fetchSymptomCheckerResults.fulfilled, (state, action) => {
        state.symptomCheckerResults = action.payload;
        state.loadingSymptomCheckerResults = false;
      })
      .addCase(fetchSymptomCheckerResults.pending, (state) => {
        state.loadingSymptomCheckerResults = true;
      })
      .addCase(fetchSymptomCheckerResults.rejected, (state) => {
        state.symptomCheckerResults = [];
        state.loadingSymptomCheckerResults = false;
      });
  },
});

export default healthJourneySlice.reducer;

export const {
  setSelectedHealthJourney,
  clearHealthPicture,
  clearTimeline,
  setCurrentHealthPicture,
  setIsOpenCreateJourneyDialog,
  setPreselectedHealthJourneyTab,
  setMyCareChangeEvent,
  clearMyCareChangeEvent,
} = healthJourneySlice.actions;
